/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../../hooks/common/use-application-state'
import { NewNoteButton } from '../../common/new-note-button/new-note-button'
import { ShowIf } from '../../common/show-if/show-if'
import { SignInButton } from '../../landing-layout/navigation/sign-in-button'
import { UserDropdown } from '../../landing-layout/navigation/user-dropdown'
import { SettingsButton } from '../../layout/settings-dialog/settings-button'
import { CheatsheetButton } from './cheatsheet/cheatsheet-button'
// import { HelpButton } from './help-button/help-button'
import { NavbarBranding } from './navbar-branding'
// import { ReadOnlyModeButton } from './read-only-mode-button'
import { SlideModeButton } from './slide-mode-button'
import { NoteType } from '@hedgedoc/commons'
import React, { useMemo, useState } from 'react'
import { Nav, Navbar, OverlayTrigger, Popover } from 'react-bootstrap'
import { PublishButton } from '../../layout/publish'
import AdminRouteButton from './admin-route-button'
import CommentButton from './comment-button'
import DraftToggleButton from './draft-button'

export enum AppBarMode {
  BASIC,
  EDITOR
}

export interface AppBarProps {
  mode: AppBarMode
}

/**
 * Renders the app bar.
 *
 * @param mode Which mode the app bar should be rendered in. This mainly adds / removes buttons for the editor.
 */
export const AppBar: React.FC<AppBarProps> = ({ mode }) => {
  const userRoles = useApplicationState((state) => state.user?.roles)
  const { noteFrontmatter, noteTitle, publishedAt }= useApplicationState((state) => ({
    noteFrontmatter: state.noteDetails.frontmatter,
    noteTitle: state.noteDetails.title,
    publishedAt: state.noteDetails.publishedAt, 
  }))
  const isAdmin = useMemo(() => userRoles?.some((role) => role.name === "admin"), [userRoles])
  const [isArticleDraftLoading, setIsArticleDraftLoading] = useState(false);

  const isArticlePublished = useMemo(() => publishedAt !== null, [publishedAt]);

  return (
    <Navbar
      expand={true}
      className={'bg-light px-3 d-flex justify-content-between'}>
      <Nav className='d-flex align-items-center'>
        <NavbarBranding />
        <ShowIf condition={mode === AppBarMode.EDITOR}>
          <ShowIf condition={noteFrontmatter.type === NoteType.SLIDE}>
            <SlideModeButton />
          </ShowIf>
          {/* <ShowIf condition={noteFrontmatter.type !== NoteType.SLIDE}>
            <ReadOnlyModeButton />
          </ShowIf>
          <HelpButton /> */}
          {/* <CheatsheetButton /> */}
        </ShowIf>
      </Nav>
      <Nav className='d-flex justify-content-center'>
        <OverlayTrigger placement="bottom" overlay={<Popover className='p-3'><h4>{noteTitle}</h4></Popover>}>
          <div className='text-center text-truncate w-75'>
            {noteTitle}
          </div>
        </OverlayTrigger>
      </Nav>
      <Nav className='d-flex gap-3 align-items-center text-secondary'>
        <ShowIf condition={isArticlePublished}>
          <DraftToggleButton isSavingDraftContent={isArticleDraftLoading} />
        </ShowIf>
        <CommentButton />
        {isAdmin ? <AdminRouteButton /> : null}
        <PublishButton
          setArticleDraftLoading={setIsArticleDraftLoading}
          variant={'outline-dark'}
          isArticlePublished={isArticlePublished}
        />
        {/* <SettingsButton variant={'outline-dark'} /> */}
        {/* <NewNoteButton /> */}
        {/* <ShowIf condition={!userExists}>
          <SignInButton size={'sm'} />
        </ShowIf>
        <ShowIf condition={userExists}>
          <UserDropdown />
        </ShowIf> */}
      </Nav>
    </Navbar>
  );
}
